import { useCallback } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  isSubscriptionError,
  getLimitErrorMessage,
  getFeatureErrorMessage,
  getErrorMessage,
  type SubscriptionError,
} from "@/lib/api/errors";

interface UseSubscriptionErrorOptions {
  tenantId: string;
  fallbackMessage?: string;
}

export function useSubscriptionError({
  tenantId,
  fallbackMessage = "An error occurred",
}: UseSubscriptionErrorOptions) {
  const navigate = useNavigate();

  const handleError = useCallback(
    (error: unknown) => {
      if (isSubscriptionError(error)) {
        const subscriptionError = error as SubscriptionError;
        const isLimitError = subscriptionError.code === "LIMIT_REACHED";

        const message = isLimitError
          ? getLimitErrorMessage(subscriptionError)
          : getFeatureErrorMessage(subscriptionError);

        const description =
          isLimitError &&
          subscriptionError.current !== undefined &&
          subscriptionError.max !== undefined
            ? `Currently using ${subscriptionError.current} of ${subscriptionError.max}`
            : undefined;

        toast.error(message, {
          description,
          dismissible: false,
          duration: 5000,
          action: subscriptionError.upgradeRequired
            ? {
                label: "Upgrade",
                onClick: () => {
                  navigate({
                    to: "/dashboard/teams/$tenantId",
                    params: { tenantId },
                    search: { tab: "subscription" },
                  });
                },
              }
            : undefined,
        });
        return true;
      }

      toast.error(getErrorMessage(error, fallbackMessage));
      return false;
    },
    [tenantId, fallbackMessage, navigate]
  );

  return { handleError };
}
